/**
 * AuthenticatedAppWrapper - Wrapper for routes that require authentication
 * 
 * This component:
 * 1. Triggers session bootstrap on mount (validates/restores player session)
 * 2. Initializes player core data via usePlayerCoreInitializer
 * 
 * Module/question loading is now handled by individual hooks (useModuleStore, useQuestionStore)
 * which auto-load when they detect an authenticated session. This ensures each component
 * gets fresh data while benefiting from modulesBundleStore's caching.
 * 
 * IMPORTANT: This is the ONLY place where bootstrap should be triggered.
 * Public routes (/, /about, /contact, etc.) should NOT use this wrapper.
 */

import React, { useEffect, useRef, useState } from 'react';
import { usePlayerAuth } from '@/hooks/usePlayerAuth';
import { useAuth } from '@/hooks/useAuth';
import { usePlayerCoreInitializer } from '@/hooks/usePlayerCoreInitializer';
import { Card } from '@/components/ui/card';
import DemoModeBanner from '@/components/system/DemoModeBanner';
import { Loader2 } from 'lucide-react';

interface AuthenticatedAppWrapperProps {
  children: React.ReactNode;
}

const AuthLoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <Card className="p-8 text-center">
      <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
      <h2 className="text-lg font-semibold mb-2">Ladataan...</h2>
      <p className="text-sm text-muted-foreground">Valmistellaan sovellusta</p>
    </Card>
  </div>
);

export const AuthenticatedAppWrapper: React.FC<AuthenticatedAppWrapperProps> = ({ children }) => {
  const { loading: authLoading } = useAuth();
  const { playerSession, loading: playerLoading, triggerSessionBootstrap } = usePlayerAuth();
  const bootstrapTriggeredRef = useRef(false);
  const [bootstrapSettled, setBootstrapSettled] = useState(false);

  usePlayerCoreInitializer();

  useEffect(() => {
    if (import.meta.env.DEV) {
      console.log('[AuthenticatedAppWrapper] mounted');
    }

    return () => {
      bootstrapTriggeredRef.current = false;
      setBootstrapSettled(false);
    };
  }, []);

  useEffect(() => {
    if (bootstrapTriggeredRef.current && playerSession?.sessionToken) return;

    let cancelled = false;
    bootstrapTriggeredRef.current = true;
    setBootstrapSettled(false);

    if (import.meta.env.DEV) {
      console.log('[AuthenticatedAppWrapper] triggering session bootstrap');
    }

    (async () => {
      try {
        await triggerSessionBootstrap();
      } finally {
        if (!cancelled) {
          setBootstrapSettled(true);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [playerSession?.sessionToken, triggerSessionBootstrap]);

  const isAuthReady = bootstrapSettled && !authLoading && !playerLoading;

  if (!isAuthReady) {
    return <AuthLoadingFallback />;
  }

  return (
    <>
      <DemoModeBanner />
      {children}
    </>
  );
};

export default AuthenticatedAppWrapper;
